import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { SpecsInputField } from "./SpecsEditorFields/index";
import { logSpecsModelPath } from "./specsModelPathLogger";
import { SPECS_DISPLAY_METHODS } from "./specsModel/displayMethods";
import type { SpecsDisplayMethod } from "./types";

type SilentMigration = {
  from: string;
  to: string;
  [key: string]: unknown;
};

type ColorCombo = {
  name: string;
  label?: string;
  colors: string[];
  [key: string]: unknown;
};

function uniqueStrings(values: Array<string | null | undefined>) {
  return Array.from(new Set(values.filter(Boolean) as string[])).sort();
}

function FieldLabel({
  label,
  debugPath,
}: {
  label: string;
  debugPath?: string;
}) {
  return (
    <Typography
      variant="caption"
      onClick={debugPath ? () => logSpecsModelPath(debugPath) : undefined}
      sx={{
        mb: 0.5,
        display: "block",
        cursor: debugPath ? "pointer" : undefined,
      }}
    >
      {label}
    </Typography>
  );
}

function getObjectValue(value: unknown, key: string) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  return (value as Record<string, unknown>)[key];
}

function toSilentMigration(value: unknown): SilentMigration | null {
  const from = getObjectValue(value, "from");
  const to = getObjectValue(value, "to");

  if (typeof from !== "string" && typeof to !== "string") {
    return null;
  }

  return {
    ...(value as Record<string, unknown>),
    from: typeof from === "string" ? from : "",
    to: typeof to === "string" ? to : "",
  };
}

function toColorCombo(value: unknown, layerCount: number): ColorCombo | null {
  const name = getObjectValue(value, "name");
  const label = getObjectValue(value, "label");
  const colors = getObjectValue(value, "colors");

  if (typeof name !== "string" && !Array.isArray(colors)) {
    return null;
  }

  const normalizedColors = Array.isArray(colors)
    ? colors.map((color) => (typeof color === "string" ? color : ""))
    : [];

  return {
    ...(value as Record<string, unknown>),
    name: typeof name === "string" ? name : "",
    ...(typeof label === "string" ? { label } : {}),
    colors: Array.from({ length: layerCount }, (_, index) => {
      return normalizedColors[index] ?? "";
    }),
  };
}

export function JsonArrayField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: unknown[];
  onChange: (value: unknown[]) => void;
}) {
  const [draft, setDraft] = React.useState(() =>
    JSON.stringify(value, null, 2)
  );
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    setDraft(JSON.stringify(value, null, 2));
  }, [value]);

  return (
    <TextField
      label={label}
      value={draft}
      onChange={(event) => {
        setDraft(event.target.value);
        setError("");
      }}
      onBlur={() => {
        try {
          const parsed = JSON.parse(draft);
          if (!Array.isArray(parsed)) {
            setError("Expected a JSON array.");
            return;
          }
          onChange(parsed);
        } catch (error) {
          setError(error instanceof Error ? error.message : "Invalid JSON.");
        }
      }}
      error={!!error}
      helperText={error ? error : " "}
      multiline
      minRows={3}
      fullWidth
      size="small"
    />
  );
}

export function BooleanField({
  label,
  checked,
  onChange,
  debugPath,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  debugPath?: string;
}) {
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      }
      label={
        <Box
          component="span"
          onClick={debugPath ? () => logSpecsModelPath(debugPath) : undefined}
          sx={{ cursor: debugPath ? "pointer" : undefined }}
        >
          {label}
        </Box>
      }
    />
  );
}

export function DisplayMethodField({
  label,
  value,
  onChange,
  debugPath,
}: {
  label: string;
  value: SpecsDisplayMethod;
  onChange: (value: SpecsDisplayMethod) => void;
  debugPath?: string;
}) {
  return (
    <Box>
      <FieldLabel label={label} debugPath={debugPath} />
      <Select
        size="small"
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value as SpecsDisplayMethod)}
      >
        {SPECS_DISPLAY_METHODS.map((method) => (
          <MenuItem key={method} value={method}>
            {method}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

export function DisplaySelect({
  label,
  value,
  options,
  onChange,
  debugPath,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  debugPath?: string;
}) {
  return (
    <Box>
      <FieldLabel label={label} debugPath={debugPath} />
      <Select
        size="small"
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

export function StringMultiSelectField({
  label,
  value,
  options,
  onChange,
  placeholder,
  debugPath,
}: {
  label: string;
  value: string[];
  options: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  debugPath?: string;
}) {
  return (
    <Box>
      <FieldLabel label={label} debugPath={debugPath} />
      <Autocomplete
        multiple
        freeSolo
        size="small"
        options={options}
        value={value}
        onChange={(_, nextValue) => onChange(uniqueStrings(nextValue))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option}
              label={option}
              size="small"
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} />
        )}
      />
    </Box>
  );
}

export function SilentMigrationField({
  label,
  value,
  styleOptions,
  onChange,
  debugPath,
}: {
  label: string;
  value: unknown[];
  styleOptions: string[];
  onChange: (value: unknown[]) => void;
  debugPath?: string;
}) {
  const migrations = value
    .map(toSilentMigration)
    .filter((migration): migration is SilentMigration => !!migration);

  const updateMigration = (index: number, patch: Partial<SilentMigration>) => {
    onChange(
      migrations.map((migration, migrationIndex) =>
        migrationIndex === index ? { ...migration, ...patch } : migration
      )
    );
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel label={label} debugPath={debugPath} />
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...migrations, { from: "", to: "" }])}
        >
          Add
        </Button>
      </Stack>
      <Stack spacing={1}>
        {migrations.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No silent migrations
          </Typography>
        ) : null}
        {migrations.map((migration, index) => (
          <Stack
            key={`${migration.from}-${migration.to}-${index}`}
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            <Autocomplete
              freeSolo
              size="small"
              options={styleOptions}
              value={migration.from}
              onChange={(_, nextValue) =>
                updateMigration(index, { from: nextValue || "" })
              }
              onInputChange={(_, nextValue, reason) => {
                if (reason === "input")
                  updateMigration(index, { from: nextValue });
              }}
              renderInput={(params) => <TextField {...params} label="From" />}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              freeSolo
              size="small"
              options={styleOptions}
              value={migration.to}
              onChange={(_, nextValue) =>
                updateMigration(index, { to: nextValue || "" })
              }
              onInputChange={(_, nextValue, reason) => {
                if (reason === "input")
                  updateMigration(index, { to: nextValue });
              }}
              renderInput={(params) => <TextField {...params} label="To" />}
              sx={{ flex: 1 }}
            />
            <IconButton
              size="small"
              onClick={() =>
                onChange(
                  migrations.filter(
                    (_, migrationIndex) => migrationIndex !== index
                  )
                )
              }
              sx={{ mt: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

export function ColorCombosField({
  label,
  value,
  layers,
  onChange,
  debugPath,
}: {
  label: string;
  value: unknown[];
  layers: string[];
  onChange: (value: unknown[]) => void;
  debugPath?: string;
}) {
  const combos = value
    .map((combo) => toColorCombo(combo, layers.length))
    .filter((combo): combo is ColorCombo => !!combo);
  const colorOptions = uniqueStrings(
    combos.flatMap((combo) => combo.colors).filter(Boolean)
  );

  const updateCombo = (index: number, patch: Partial<ColorCombo>) => {
    onChange(
      combos.map((combo, comboIndex) =>
        comboIndex === index ? { ...combo, ...patch } : combo
      )
    );
  };
  const updateComboColor = (
    comboIndex: number,
    colorIndex: number,
    color: string
  ) => {
    const combo = combos[comboIndex];
    if (!combo) return;
    const colors = [...combo.colors];
    colors[colorIndex] = color;
    updateCombo(comboIndex, { colors });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel label={label} debugPath={debugPath} />
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() =>
            onChange([
              ...combos,
              { name: "", label: "", colors: layers.map(() => "") },
            ])
          }
        >
          Add
        </Button>
      </Stack>
      <Stack spacing={1.5}>
        {combos.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No color combos
          </Typography>
        ) : null}
        {combos.map((combo, comboIndex) => (
          <Box
            key={`${combo.name}-${comboIndex}`}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "6px",
              p: 1.5,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <SpecsInputField
                label="Name"
                debugPath={
                  debugPath ? `${debugPath}[${comboIndex}].name` : undefined
                }
                value={combo.name}
                onChange={(event) =>
                  updateCombo(comboIndex, { name: event.target.value })
                }
                sx={{ flex: 1 }}
              />
              <SpecsInputField
                label="Label"
                debugPath={
                  debugPath ? `${debugPath}[${comboIndex}].label` : undefined
                }
                value={combo.label || ""}
                onChange={(event) =>
                  updateCombo(comboIndex, { label: event.target.value })
                }
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() =>
                  onChange(combos.filter((_, index) => index !== comboIndex))
                }
                sx={{ mt: 0.5 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1,
                mt: 1,
              }}
            >
              {layers.map((layer, colorIndex) => (
                <Autocomplete
                  key={`${comboIndex}-${layer}`}
                  freeSolo
                  size="small"
                  options={colorOptions}
                  value={combo.colors[colorIndex] || ""}
                  onChange={(_, nextValue) =>
                    updateComboColor(comboIndex, colorIndex, nextValue || "")
                  }
                  onInputChange={(_, nextValue, reason) => {
                    if (reason === "input") {
                      updateComboColor(comboIndex, colorIndex, nextValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={layer || `Layer ${colorIndex + 1}`}
                    />
                  )}
                />
              ))}
            </Box>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
